﻿import { uniq } from "lodash";
import { DateTime, type DateTimeOptions, Duration } from "luxon";
import { getBrowserIanaTimeZone } from "./browser-time-zone";
import { Enums } from "./enums";
import { LocalStorageService } from "./local-storage-service";
import { customStatusEntity } from "../model/entity/custom-status-entity";
import {
  formatPortalDateTime,
  formatPortalDateWithTodayYesterday,
  formatPortalSidebarListTime,
  formatPortalUtcCalendarDate,
} from "./portal-datetime-format";

export class CommonHelper {
  static getDateWithTodayYesterdayFormat(
    date: string,
    prependText: string = "",
  ): string {
    return formatPortalDateWithTodayYesterday(date, prependText);
  }

  /**
   * Compact activity time for sidebars (relative for recent, short absolute for older).
   * `nowMs` supports tests and a reactive tick so labels refresh without entity updates.
   */
  static formatSidebarListActivityAt(
    date: string,
    nowMs: number = Date.now(),
  ): string {
    return formatPortalSidebarListTime(date, nowMs);
  }

  static getInitials(firstName: string, lastName: string) {
    let firstNameInitial = "";
    let lastNameInitial = "";
    if (firstName != null && firstName !== "") {
      let acronym = firstName
        .split(/\s/)
        .reduce((response, word) => (response += word.slice(0, 1)), "");
      firstNameInitial = acronym.toUpperCase()[0];
    }
    if (lastName != null && lastName !== "") {
      let acronym = lastName
        .split(/\s/)
        .reduce((response, word) => (response += word.slice(0, 1)), "");
      lastNameInitial = acronym.toUpperCase()[0];
    }
    const initials = firstNameInitial + lastNameInitial;
    if (initials != "") {
      return initials;
    }
    return "N/A";
  }

  static getHoursMinutesFormatFromMinutes(timeInMinutes: number): string {
    let hoursAndMinutes = "";
    if (timeInMinutes > 59) {
      hoursAndMinutes = Duration.fromObject({
        minutes: timeInMinutes,
      }).toFormat("hh:mm");
    } else if (timeInMinutes < 1) {
      hoursAndMinutes = "00:00";
    } else {
      hoursAndMinutes =
        "00:" + Duration.fromObject({ minutes: timeInMinutes }).toFormat("mm");
    }
    return hoursAndMinutes;
  }

  static getHoursMinutesFormatFromHours(timeInHours: number): string {
    let totalMinutes = timeInHours * 60;
    let hours = Math.floor(totalMinutes / 60);
    let minutes = totalMinutes % 60;
    if (hours < 24) {
      let hoursData = hours < 10 ? "0" + hours : hours;
      let minutesData: string = minutes < 10 ? "0" + minutes : "" + minutes;
      return `${hoursData}:${minutesData.substring(0, 2)}`;
    }
    return "00:00";
  }

  static getTimeIn24HourFormat(timeIn12HourFormat: string): string {
    let time = timeIn12HourFormat.substring(0, 5);
    let AMPM = timeIn12HourFormat.substring(5);
    let timeArray = time.split(":");
    let hours: number = parseInt(timeArray[0]);
    let minutes: number = parseInt(timeArray[1]);
    if (AMPM == "PM" && hours < 12) {
      hours = hours + 12;
    }
    if (AMPM == "AM" && hours == 12) {
      hours = hours - 12;
    }
    let sHours = hours.toString();
    let sMinutes = minutes.toString();
    if (hours < 10) {
      sHours = "0" + sHours;
    }
    if (minutes < 10) {
      sMinutes = "0" + sMinutes;
    }
    return `${sHours}:${sMinutes}`;
  }

  static getTimeIn12HourFormat(time: string): string {
    let timeArray = time.split(":");
    let hour: any = timeArray[0];
    let minute: any = timeArray[1];
    let ap = "AM";
    if (hour > 11) {
      ap = "PM";
    }
    if (hour > 12) {
      hour = hour - 12;
    }
    if (hour == 0) {
      hour = 12;
    }
    if (hour < 10 && hour.toString().length == 1) {
      hour = "0" + hour;
    }
    if (minute < 10 && minute.toString().length == 1) {
      minute = "0" + minute;
    }
    const timeString = hour + ":" + minute + ap;
    return timeString;
  }

  static getTimeIn12HourFormatFromDate(date: string): string {
    let time = DateTime.fromISO(date)
      .setZone(getBrowserIanaTimeZone())
      .setLocale(navigator.language)
      .toLocaleString(DateTime.TIME_SIMPLE)
      .toUpperCase();
    let hour = time.split(":")[0];
    if (hour.length == 1) {
      time = "0" + time;
    }
    return time;
  }

  static getTicketStatusSortOrder(statusId: number): number {
    let ticketStatus = customStatusEntity.find(statusId) as any;
    if (
      ticketStatus &&
      ticketStatus.baseStatusId != ticketStatus.ticketStatusId
    ) {
      statusId = ticketStatus.baseStatusId;
    }

    switch (statusId) {
      case Enums.TicketStatus.New:
        return 1;
      case Enums.TicketStatus.Open:
        return 2;
      case Enums.TicketStatus.OnHold:
        return 4;
      case Enums.TicketStatus.Solved:
        return 5;
      case Enums.TicketStatus.Closed:
        return 6;
      default:
        return 100;
    }
  }

  static getImageUrlFromThumbnailUrl(thumbnailUrl: string): string {
    if (thumbnailUrl.indexOf("/thumb/") != -1) {
      thumbnailUrl = thumbnailUrl.replace("/thumb", "");
      let url = thumbnailUrl.substring(0, thumbnailUrl.indexOf("?"));
      const extension = url.substr(url.lastIndexOf("."));
      let originalUrl = `${url.substring(
        0,
        thumbnailUrl.lastIndexOf("_"),
      )}${extension}`;
      return originalUrl;
    }
    return "";
  }

  static getShipmentClass(status: string): string {
    if (status == "InfoReceived") return "InfoRecieved";

    if (status == "InTransit") return "InTransit";

    if (status == "OutForDelivery") return "OutOfDelivery";

    if (status == "AttemptFail") return "FailedAttempt";

    if (status == "Delivered") return "Delivered";

    if (status == "AvailableForPickup") return "Pickup";

    if (status == "Exception") return "Exception";

    if (status == "Expired") return "Expired";

    if (status == "Pending") return "Pending";

    return "InfoRecieved";
  }

  static getShipmentStatusText(status: string): string {
    if (status == "InfoReceived") return "Info Received";

    if (status == "InTransit") return "In Transit";

    if (status == "OutForDelivery") return "Out for Delivery";

    if (status == "AttemptFail") return "Failed Attempt";

    if (status == "Delivered") return "Delivered";

    if (status == "AvailableForPickup") return "Available for Pickup";

    if (status == "Exception") return "Exception";

    if (status == "Expired") return "Expired";

    if (status == "Pending") return "Pending";

    return "";
  }

  //***************************
  //***************************
  //Billing Objects

  static getCheckBillingTerm(value: any) {
    switch (value) {
      case 1:
        return "Monthly";
      case 2:
        return "Quarterly";
      case 3:
        return "Yearly";

      default:
        return "Monthly";
    }
  }

  static getCheckBillingStatus(value: any) {
    switch (value) {
      case 1:
        return "Billable";
      case 2:
        return "Not Billable";
      case 3:
        return "Not Billable (Hidden)";
      case 5:
        return "Write Off";

      default:
        return "Billable";
    }
  }

  static getCheckBillingPaymentTerm(value: any) {
    switch (value) {
      case 1:
        return "net 7";
      case 2:
        return "net 10";
      case 3:
        return "net 15";
      case 4:
        return "net 30";
      case 5:
        return "Due Upon Receipt";

      default:
        return "7";
    }
  }

  static getCheckBillingRecurringDate(value: any) {
    switch (value) {
      case 0:
        return "last day of month";

      default:
        return (
          " " + CommonHelper.getCustomMonthLabel(value) + " of every month"
        );
    }
  }

  static getCustomMonthLabel(label: string) {
    if (label && label != "") {
      let customDate = parseInt(label);
      let s = ["th", "st", "nd", "rd"];
      let v = customDate % 100;
      let customLabel = `${customDate + (s[(v - 20) % 10] || s[v] || s[0])}`;
      return customLabel;
    }
    return label;
  }

  static tryParseJSONObject(jsonString: string) {
    //This function will return `false` for any valid json primitive.
    //EG, 'true' -> false
    //'123' -> false
    //'null' -> false
    //'"I'm a string"' -> false

    try {
      const o = JSON.parse(jsonString);

      // Handle non-exception-throwing cases:
      // Neither JSON.parse(false) or JSON.parse(1234) throw errors, hence the type-checking,
      // but... JSON.parse(null) returns null, and typeof null === "object",
      // so we must check for that, too. Thankfully, null is falsey, so this suffices:
      if (o && typeof o === "object") {
        return o;
      }
    } catch (e) {}

    return false;
  }

  static getTimeLineIconClass(value: any) {
    let type: string = value.type;
    let ticketTimeLineType: string = value.ticketTimeLineType;
    let isPrimary: boolean = value.isPrimary;

    switch (type) {
      case "Event":
        if (
          ticketTimeLineType == "Technician" ||
          ticketTimeLineType == "SecondaryTechnician" ||
          ticketTimeLineType == "Watcher"
        ) {
          return "far fa-user-headset";
        } else if (ticketTimeLineType == "Merged") {
          return "fal fa-layer-plus";
        } else if (ticketTimeLineType == "TodoItem") {
          return "far fa-tasks";
        } else if (ticketTimeLineType == "TicketSchedule") {
          return "fal fa-calendar-alt";
        } else if (ticketTimeLineType == "Acknowledge") {
          return "fal fa-thumbs-up";
        }
        break;
      case "Comment":
        if (isPrimary) {
          return "far fa-envelope";
        } else {
          let isPublicComment: boolean =
            !value.isInternal &&
            (value.thirdPartyConversationId == null ||
              value.thirdPartyConversationId == 0);
          if (isPublicComment) {
            return "far fa-envelope";
          } else {
            return "far fa-comment";
          }
        }

      case "TimeSpent":
        return "far fa-clock";
      case "Product":
        return "fal fa-box-alt";
      case "ThirdPartyConversation":
        return "far fa-envelope";
      default:
        return "far fa-heart-rate";
    }
    return "far fa-heart-rate";
  }

  static getClassForTicketTimeLine(value: any) {
    let classToShowWhenCommentExpanded: string = "";
    if (value.showMoreComment) {
      classToShowWhenCommentExpanded = " show-less-text";
    }
    let newCommentClass =
      value.isNewComment == true ? " warningxLight borderwarning border1p" : "";
    if (value.thirdPartyConversationId > 0) {
      return (
        "internalcomment thirdpartycommentbox timlineComment primary font--text userComment transition-ease-2s" +
        newCommentClass +
        classToShowWhenCommentExpanded
      );
    } else if (value.isInternal) {
      return (
        "timlineComment primary font--text userComment transition-ease-2s" +
        newCommentClass +
        classToShowWhenCommentExpanded
      );
    }
    return (
      "timlineComment internalcomment primary font--text userComment transition-ease-2s" +
      newCommentClass +
      classToShowWhenCommentExpanded
    );
  }

  static isStringsEquals(string1: string, string2: string) {
    if (!string1) string1 = "";
    if (!string2) string2 = "";
    return string1 == string2;
  }

  static isDebugModeEnabled(): boolean {
    return LocalStorageService.getItem("debugMode") != null;
  }

  static onKeyPressCheckNumber(event: any) {
    if (
      (event &&
        event.keyCode == 46 &&
        (event.currentTarget.value.indexOf(".") != -1 ||
          (event.currentTarget.value.length == 7 &&
            event.target.selectionStart == 7))) ||
      !/[\d+.]/.test(event.key)
    ) {
      event.preventDefault();
    }
  }

  static highlightSearchedTermInText(
    searchedQuery: string,
    text: string,
  ): string {
    searchedQuery = searchedQuery.toLowerCase();
    //If there are multiple words in the searched term
    if (searchedQuery.indexOf(" ") != -1) {
      let queryArray: string[] = searchedQuery.split(" ");
      queryArray = uniq(queryArray);

      for (let keyword of queryArray) {
        const regex = new RegExp(keyword, "ig");
        text = text.replace(
          regex,
          "<span class='secondary font--text'>" + keyword + "</span>",
        );
      }
    }
    //If there is only one word
    else {
      const regex = new RegExp(searchedQuery, "ig");
      text = text.replace(
        regex,
        "<span class='secondary font--text'>" + searchedQuery + "</span>",
      );
    }

    let firstIndexOfHighlightedTerm: number = text.indexOf(
      "<span class='secondary font--text'>",
    );

    if (firstIndexOfHighlightedTerm > 20) {
      text = "..." + text.substring(firstIndexOfHighlightedTerm - 20);
    }

    return text;
  }

  static dateTimeFormatIntl(value: any): string {
    return formatPortalDateTime(value);
  }

  static hashCode(value: string) {
    let hash = 0;
    if (value.length === 0) return hash;
    for (let i = 0; i < value.length; i++) {
      const chr = value.charCodeAt(i);
      hash = (hash << 5) - hash + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  static FormatDateToDateShort(
    date: string,
    dateTimeOptions: DateTimeOptions = { zone: "utc" },
  ) {
    return formatPortalUtcCalendarDate(date, dateTimeOptions);
  }

  static replaceSingleQuoteWithDouble(inputString: string): string {
    return inputString && inputString != ""
      ? inputString.replace(/"/g, '""')
      : "";
  }
}
